import { Modal } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Input } from "../../../../../_metronic/_partials/controls";
import {
  getProductCategories,
  setProductCategoryItem,
} from "../../../api/ProductsApi";
import * as Yup from "yup";
import { useState, useEffect, useMemo } from "react";
import { getCatalogProductCodes } from "../../../api/ProductCatalogApi";
import { IProductCatalogLookupDto } from "../../../models/ProductCatalog";

const validationSchema = Yup.object().shape({
  lengthMm: Yup.number()
    .required("Length (mm) is required")
    .min(0, "Length must be greater than or equal to 0"),
  breadthMm: Yup.number()
    .required("Breadth (mm) is required")
    .min(0, "Breadth must be greater than or equal to 0"),
  heightMm: Yup.number()
    .required("Height (mm) is required")
    .min(0, "Height must be greater than or equal to 0"),
  weightGr: Yup.number()
    .required("Weight (gr) is required")
    .min(0, "Weight must be greater than or equal to 0"),
  articleCode: Yup.string().required("Product Code is required"),
});

interface IVersionedModifierOption {
  key: string;
  modifierName: string;
}

function getAvailableVersionedModifiers(
  productSpecsModifiers: { name: string; modifierType: string }[]
): IVersionedModifierOption[] {
  return productSpecsModifiers
    .filter(m =>
      m.modifierType === "ProductOptionBased" &&
      // Name must have at least 3 parts when split by "-" (e.g. "GiftPackaging-GiftBox-Box2")
      // First 2 parts form the modifier key and are used for grouping
      m.name.split("-").length >= 3)
    .map(m => {
      const parts = m.name.split("-");
      const key = parts.slice(0, 2).join("-");
      return { key: key, modifierName: m.name };
    });
}

function getLinkedVersionedModifiers(
  versionedModifierNames: Record<string, string[]> | undefined
): IVersionedModifierOption[] {
  if (!versionedModifierNames) return [];
  const result: IVersionedModifierOption[] = [];
  for (const [key, values] of Object.entries(versionedModifierNames)) {
    if (!Array.isArray(values)) continue;
    for (const modName of values) {
      result.push({ key, modifierName: modName });
    }
  }
  return result;
}

const ProductCategoryItemAddForm = ({
  onHide,
  onSuccess,
  plant,
  productGroup,
  productType,
  singleProduct,
  productCategoryData,
}: any) => {

  const [papPairs, setPapPairs] = useState<Array<{ key: string; description: string }>>([]);

  const availableVersionedModifiers = useMemo(() => {
    const modifiers = productCategoryData?.productSpecsModifiers || [];
    return getAvailableVersionedModifiers(modifiers);
  }, [productCategoryData]);

  const linkedVersionedModifiers = useMemo(() => {
    return getLinkedVersionedModifiers(singleProduct?.versionedModifierNames);
  }, [singleProduct]);

  const allVersionedModifiers = useMemo(() => {
    const seen = new Set<string>();
    const result: IVersionedModifierOption[] = [];
    for (const item of [...availableVersionedModifiers, ...linkedVersionedModifiers]) {
      if (!seen.has(item.modifierName)) {
        seen.add(item.modifierName);
        result.push(item);
      }
    }
    return result;
  }, [availableVersionedModifiers, linkedVersionedModifiers]);

  const groupedModifiers: [string, string[]][] = useMemo(() => {
    const groups: Record<string, string[]> = {};
    for (const { key, modifierName } of allVersionedModifiers) {
      if (!groups[key]) groups[key] = [];
      groups[key].push(modifierName);
    }
    return Object.entries(groups);
  }, [allVersionedModifiers]);

  const initialSelected = useMemo(() => {
    const selected = new Set<string>();
    const existing = singleProduct?.versionedModifierNames || {};
    for (const values of Object.values(existing)) {
      for (const name of (values as string[])) {
        selected.add(name);
      }
    }
    return selected;
  }, [singleProduct]);

  const [selectedModifierNames, setSelectedModifierNames] = useState<Set<string>>(initialSelected);
  const [showAdvanced, setShowAdvanced] = useState(initialSelected.size > 0);

  useEffect(() => {
    loadProductCodes(productType).then(setPapPairs).catch(error => console.error('Failed to load product codes:', error));
  }, [productType]);

  const loadProductCodes = (productType: string): Promise<{ key: string, description: string }[]> => {
    return getCatalogProductCodes(productType).then((results: IProductCatalogLookupDto[]) => {
      return results.map((item: IProductCatalogLookupDto) => ({
        key: item.key,
        description: item.description,
      }));
    });
  };

  const toggleModifier = (modifierName: string) => {
    setSelectedModifierNames(prev => {
      const next = new Set(prev);
      if (next.has(modifierName)) {
        next.delete(modifierName);
      } else {
        next.add(modifierName);
      }
      return next;
    });
  };

  const buildProductOptionsBasedModifierNames = (): Record<string, string[]> => {
    const result: Record<string, string[]> = {};
    for (const { key, modifierName } of allVersionedModifiers) {
      if (selectedModifierNames.has(modifierName)) {
        if (!result[key]) result[key] = [];
        result[key].push(modifierName);
      }
    }
    return result;
  };

  const hasVersionedModifiers = groupedModifiers.length > 0;

  return (
    <>
      <Formik
        initialValues={{
          lengthMm: singleProduct?.size?.lengthMm,
          breadthMm: singleProduct?.size?.breadthMm,
          heightMm: singleProduct?.size?.heightMm,
          weightGr: singleProduct?.weightGr,
          articleCode: singleProduct?.articleCode,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setStatus }) => {
          try {
            await setProductCategoryItem(plant, productGroup, {
              articles: [
                {
                  size: {
                    lengthMm: values.lengthMm,
                    breadthMm: values.breadthMm,
                    heightMm: values.heightMm,
                    weightGr: values.weightGr,
                  },
                  articleCode: values.articleCode,
                  productOptionsBasedModifierNames: buildProductOptionsBasedModifierNames(),
                  productOptionsOverrides: singleProduct?.additionalModifiers ?? [],
                },
              ],
            });
            setStatus("success");
            setTimeout(() => {
              onHide();
              onSuccess();
            }, 1500);
            getProductCategories(plant);
          } catch (error) {
            setStatus("error");
          }
        }}
      >
        {({ handleSubmit, handleChange, status }) => (
          <>
            <Modal.Body className="overlay overlay-block cursor-default">
              <Form className="form form-label-right">
                <div className="form-group">
                  <div>
                  <Field name="articleCode" as="select" 
                   placeholder="ex. PAP_333"
                   label="(Product Category Item)"
                   onChange={handleChange}
                   disabled={!!singleProduct}
                   >
                    <option>(select product code)</option>
                    {papPairs.map((dto: {key: string, description: string}) => (
                      <option value={dto.key}>{dto.description}</option>
                    )
                    )}
                  </Field>
                  <div className="text-danger">
                    {productType === undefined && (
                      <span>No product codes found. Please specify a Product Type for this group (use Pencil icon from Groups Overview page)</span>
                    )}
                    <ErrorMessage
                      name="articleCode"
                      />
                  </div>
                </div>
                </div>
                <div className="form-group row">
                  <div className="col-lg-4">
                    <Field
                      name="lengthMm"
                      component={Input}
                      placeholder="Length(mm)"
                      label="Length(mm)"
                      type="number"
                    />
                  </div>
                  <div className="col-lg-4">
                    <Field
                      name="breadthMm"
                      component={Input}
                      placeholder="Width(mm)"
                      label="Width(mm)"
                      type="number"
                    />
                  </div>
                  <div className="col-lg-4">
                    <Field
                      name="heightMm"
                      component={Input}
                      placeholder="Height(mm)"
                      label="Height(mm)"
                      type="number"
                    />
                  </div>
                  <div className="col-lg-4">
                    <Field
                      name="weightGr"
                      component={Input}
                      placeholder="weightGr"
                      label="weightGr"
                      type="number"
                    />
                  </div>
                </div>
              </Form>
              {showAdvanced && (
                <div className="mt-4">
                  <hr />
                  <div className="font-weight-bold mb-4">
                    Versioned Modifiers
                  </div>
                  {!hasVersionedModifiers && (
                    <div className="text-muted p-3">
                      No versioned modifiers available.
                    </div>
                  )}
                  {hasVersionedModifiers && groupedModifiers.map(([key, modifierNames]) => (
                    <div key={key} className="mb-3">
                      <div className="text-muted font-weight-bold small mb-1">
                        {key}
                      </div>
                      {modifierNames.map(modifierName => (
                        <div key={modifierName} className="form-check ml-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`vm-${modifierName}`}
                            checked={selectedModifierNames.has(modifierName)}
                            onChange={() => toggleModifier(modifierName)}
                          />
                          <label className="form-check-label" htmlFor={`vm-${modifierName}`}>
                            {modifierName}
                          </label>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-light btn-elevate"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? "▲ Advanced" : "▼ Advanced"}
              </button>
              <div>
                <button
                  type="button"
                  onClick={onHide}
                  className="btn btn-light btn-elevate mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={() => {
                    handleSubmit();
                  }}
                  className="btn btn-primary btn-elevate"
                >
                  Save
                </button>
              </div>
            </Modal.Footer>
            {status === "success" && (
              <div className="text-success text-center mt-3 p-10">
                Saved successfully!
              </div>
            )}
            {status === "error" && (
              <div className="text-danger text-center mt-3 p-10">
                An error occurred.
              </div>
            )}
          </>
        )}
      </Formik>
    </>
  );
};

export default ProductCategoryItemAddForm;
